import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, createTextVNode as _createTextVNode, mergeProps as _mergeProps, withCtx as _withCtx, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps, unref as _unref } from "vue"

const _hoisted_1 = { class: "d-flex align-center" }
const _hoisted_2 = {
  key: 0,
  class: "d-flex flex-column align-center ml-2 mb-5"
}
const _hoisted_3 = { class: "mb-1" }
const _hoisted_4 = { class: "pl-4 ma-0" }
const _hoisted_5 = { key: 0 }
const _hoisted_6 = { class: "d-flex align-center" }
const _hoisted_7 = { class: "d-flex align-center" }
const _hoisted_8 = { class: "d-flex align-center mt-n4" }
const _hoisted_9 = { class: "d-flex align-center mb-4" }
const _hoisted_10 = { class: "d-flex align-center" }
const _hoisted_11 = { class: "d-flex align-center" }
const _hoisted_12 = { class: "d-flex align-center" }
const _hoisted_13 = { class: "d-flex align-center" }
const _hoisted_14 = { class: "flex justify-end" }

import { computed, PropType, ref, onMounted, nextTick, watch } from 'vue';
import { VDateInput } from 'vuetify/labs/components';
import type { PriceListForm } from '@/types/priceList';
import { getCustomers } from '@/services/customer.service';
import { dispatchToast } from '@/utils/dispatchToast';
import { getDateInAuroraTimeZone } from '@/utils/priceListUtils';

interface OverlappingPriceList {
  id: string;
  name: string;
  tag?: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'PriceListInfo',
  props: {
  form: {
    type: Object as PropType<PriceListForm>,
    required: true
  },
  appendMode: {
    type: Boolean,
    default: true
  },
  isSaveDisabled: {
    type: Boolean,
    default: false
  },
  isEmptyPricelist: {
    type: Boolean,
    default: false
  },
  isLimitedVersion: {
    type: Boolean,
    default: false
  },
  overlappingPricelists: {
    type: Array as PropType<OverlappingPriceList[]>,
    default: () => []
  },
  defaultOnPortal: {
    type: Boolean,
    default: false
  }
},
  emits: [
  'update:form',
  'save',
  'preview',
  'cancel'
],
  setup(__props, { emit: __emit }) {

const props = __props;

const emit = __emit;

const allPartners = ref([]);
const startDate = ref(null);
const endDate = ref(null);
const partnersField = ref(null);

// Because offset is different depending on the date (due to daylight saving time), we need to calculate it for each date
const getAuroraOffset = (date: Date) => {
  const offsetInMs = new Date(date.toLocaleString('en-US', { timeZone: "America/Chicago" })).getTime()
    - new Date(date.toLocaleString('en-US', { timeZone: "UTC" })).getTime();
  const offset = Math.round(offsetInMs / (1000 * 60 * 60));
  return offset >= 0 ? `+${offset.toString().padStart(2, '0')}` : `-${Math.abs(offset).toString().padStart(2, '0')}`;
};

const startofDayAuroraTime = (date: Date) => {
  const offset = getAuroraOffset(date);
  const year = date.getFullYear();
  const month = date.getMonth() + 1 < 10 ? `0${date.getMonth() + 1}` : date.getMonth() + 1;
  const day = date.getDate() < 10 ? `0${date.getDate()}` : date.getDate();
  return new Date(`${year}-${month}-${day} 00:00:00.000${offset}`);
};

const endOfDayAuroraTime = (date: Date) => {
  const offset = getAuroraOffset(date);
  const year = date.getFullYear();
  const month = date.getMonth() + 1 < 10 ? `0${date.getMonth() + 1}` : date.getMonth() + 1;
  const day = date.getDate() < 10 ? `0${date.getDate()}` : date.getDate();
  return new Date(`${year}-${month}-${day} 23:59:59.999${offset}`);
};

const formatDates = () => {
  if (startDate.value) {
    formModel.value.startDate = startofDayAuroraTime(new Date(startDate.value)).toISOString();
  }
  if (endDate.value) {
    formModel.value.endDate = endOfDayAuroraTime(new Date(endDate.value)).toISOString();
  }
  return true;
};


const yesterdayAuroraTime = () => {
  const date = new Date();
  date.setDate(date.getDate() - 1);
  return new Date(date.toLocaleString('en-US', { timeZone: 'America/Chicago' }));
};

const currentAuroraTimeString = () => new Date().toLocaleString('en-US', { timeZone: 'America/Chicago' });

// Create a computed property for two-way binding
const formModel = computed({
  get: () => props.form,
  set: (value) => emit('update:form', value)
});

const selectedFirstPartners = computed(() => {
  const selectedIds = formModel.value.partners.map((partner) => partner.id);
  const selected = allPartners.value.filter((partner) => selectedIds.includes(partner.id));
  const rest = allPartners.value.filter((partner) => !selectedIds.includes(partner.id));
  return [...selected, ...rest];
});

// Methods
const validateDateRange = () => {
  if (!formModel.value.startDate || !formModel.value.endDate) {
    return true;
  }
  return new Date(formModel.value.startDate) < new Date(formModel.value.endDate) || 'Start date must be before end date';
};

const handleFileClear = () => {
  const updatedForm = { ...formModel.value, file: undefined };
  emit('update:form', updatedForm);
};

const handleCancel = () => {
  emit('cancel');
};

const handleSaveOrPreview = () => {
  if (formModel.value.file) {
    emit('preview');
  } else {
    emit('save');
  }
};

const fetchPartners = async () => {
  try {
    const { success, data } = await getCustomers(
      { attributes: JSON.stringify(['id', 'name', 'active', 'relationshipSegment']) },
      true,
    );
    if (success) {
      allPartners.value = data;
    } else {
      throw new Error('Error fetching partners');
    }
  } catch (error) {
    dispatchToast('Error fetching partners', { type: 'error' });
  }
}

// Either partners or end users must be specified (an empty field matches everything)
const requirePartnerOrEndUser = () =>
  !!formModel.value.partners.length || !!formModel.value.endUsers.length
  || 'Specify at least one partner or end user';

// End users are free-text strings; normalize on every change (trim + dedupe, keep insertion order)
const onEndUsersUpdate = (values: unknown[]) => {
  const cleaned: string[] = [];
  for (const raw of values) {
    const value = (typeof raw === 'string' ? raw : String(raw ?? '')).trim();
    if (value && !cleaned.includes(value)) {
      cleaned.push(value);
    }
  }
  formModel.value.endUsers = cleaned;
};

const validatePartners = async () => {
  await nextTick();
  const selectedPartners = formModel.value.partners;
  const validPartnerNames = allPartners.value.map((partner) => partner.name);

  const correctPartners = [];
  for (const partner of selectedPartners) {
    // Due to combobox search behavior, we need to check for exact name match and select it, otherwise dismiss it
    if (typeof partner === 'string') {
      const exactMatch = allPartners.value.find((p) => p.name === partner);
      if (exactMatch) {
        correctPartners.push(exactMatch);
      }
    } else if (partner.name && validPartnerNames.includes(partner.name)) {
      correctPartners.push(partner);
    }
  }
  formModel.value.partners = correctPartners;
};

onMounted(async () => {
  if (formModel.value.startDate) {
    startDate.value = new Date(formModel.value.startDate).toLocaleString('en-US', { timeZone: 'America/Chicago' });
  }
  if (formModel.value.endDate) {
    endDate.value = new Date(formModel.value.endDate).toLocaleString('en-US', { timeZone: 'America/Chicago' });
  }
  await fetchPartners();
});

watch(() => formModel.value.startDate, (newStartDate) => {
  startDate.value = getDateInAuroraTimeZone(newStartDate);
});

watch(() => formModel.value.endDate, (newEndDate) => {
  endDate.value = getDateInAuroraTimeZone(newEndDate);
});

// The partner/end-user requirement is cross-field, so re-validate the partners field when end users change
watch(() => formModel.value.endUsers.length, () => {
  nextTick(() => partnersField.value?.validate());
});

return (_ctx: any,_cache: any) => {
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_tooltip = _resolveComponent("v-tooltip")!
  const _component_v_checkbox_btn = _resolveComponent("v-checkbox-btn")!
  const _component_v_list_item = _resolveComponent("v-list-item")!
  const _component_v_combobox = _resolveComponent("v-combobox")!
  const _component_v_checkbox = _resolveComponent("v-checkbox")!
  const _component_v_file_input = _resolveComponent("v-file-input")!
  const _component_v_radio = _resolveComponent("v-radio")!
  const _component_v_radio_group = _resolveComponent("v-radio-group")!
  const _component_v_textarea = _resolveComponent("v-textarea")!
  const _component_v_btn = _resolveComponent("v-btn")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(_component_v_text_field, {
      modelValue: formModel.value.name,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((formModel.value.name) = $event)),
      label: "Price List Name",
      rules: [() => !!formModel.value.name || 'Name is required']
    }, null, 8, ["modelValue", "rules"]),
    _createElementVNode("div", _hoisted_1, [
      _createVNode(_component_v_text_field, {
        modelValue: formModel.value.tag,
        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((formModel.value.tag) = $event)),
        label: "Price List Tag",
        class: "flex-grow-1"
      }, null, 8, ["modelValue"]),
      (__props.overlappingPricelists && __props.overlappingPricelists.length > 0)
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
            (__props.overlappingPricelists && __props.overlappingPricelists.length > 0)
              ? (_openBlock(), _createBlock(_component_v_tooltip, {
                  key: 0,
                  location: "right",
                  "max-width": "350"
                }, {
                  activator: _withCtx(({ props }) => [
                    _createVNode(_component_v_icon, _mergeProps(props, {
                      color: "warning",
                      style: {"font-size":"20px"}
                    }), {
                      default: _withCtx(() => _cache[11] || (_cache[11] = [
                        _createTextVNode("mdi-link-box-variant")
                      ])),
                      _: 2
                    }, 1040)
                  ]),
                  default: _withCtx(() => [
                    _createElementVNode("div", null, [
                      _createElementVNode("div", _hoisted_3, "This pricelist overlaps with " + _toDisplayString(__props.overlappingPricelists.length) + " other pricelist" + _toDisplayString(__props.overlappingPricelists.length > 1 ? 's' : '') + ":", 1),
                      _createElementVNode("ul", _hoisted_4, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.overlappingPricelists, (pl) => {
                          return (_openBlock(), _createElementBlock("li", {
                            key: pl.id
                          }, [
                            _createTextVNode(_toDisplayString(pl.name), 1),
                            (pl.tag)
                              ? (_openBlock(), _createElementBlock("span", _hoisted_5, " (" + _toDisplayString(pl.tag) + ")", 1))
                              : _createCommentVNode("", true)
                          ]))
                        }), 128))
                      ])
                    ])
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true),
            (__props.defaultOnPortal)
              ? (_openBlock(), _createBlock(_component_v_tooltip, {
                  key: 1,
                  location: "right",
                  "max-width": "300"
                }, {
                  activator: _withCtx(({ props }) => [
                    _createElementVNode("div", _mergeProps(props, { class: "d-flex align-center mt-2" }), [
                      _createVNode(_component_v_icon, {
                        size: "20",
                        color: "grey"
                      }, {
                        default: _withCtx(() => _cache[12] || (_cache[12] = [
                          _createTextVNode("mdi-checkbox-marked")
                        ])),
                        _: 1
                      })
                    ], 16)
                  ]),
                  default: _withCtx(() => [
                    _cache[13] || (_cache[13] = _createElementVNode("span", null, "This is the pricelist marked as default for the prices shown in the 360 portal", -1))
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true)
          ]))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", _hoisted_6, [
      _createVNode(_component_v_combobox, {
        class: "flex-grow-1",
        ref_key: "partnersField",
        ref: partnersField,
        modelValue: formModel.value.partners,
        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((formModel.value.partners) = $event)),
        items: selectedFirstPartners.value,
        "item-title": "name",
        "item-value": "id",
        "auto-select-first": "exact",
        label: "Partners",
        multiple: "",
        "menu-props": { maxWidth: 0, minHeight: 400 },
        rules: [requirePartnerOrEndUser],
        onBlur: validatePartners
      }, {
        item: _withCtx(({ item, props }) => [
          _createVNode(_component_v_list_item, _normalizeProps(_guardReactiveProps(props)), {
            prepend: _withCtx(({ isSelected }) => [
              _createVNode(_component_v_checkbox_btn, { "model-value": isSelected }, null, 8, ["model-value"])
            ]),
            title: _withCtx(() => [
              _createVNode(_component_v_tooltip, {
                location: "right",
                text: item.raw.name,
                "open-delay": 1000
              }, {
                activator: _withCtx(({ props: tooltipProps }) => [
                  _createElementVNode("div", _normalizeProps(_guardReactiveProps(tooltipProps)), _toDisplayString(item.raw.name), 17)
                ]),
                _: 2
              }, 1032, ["text"])
            ]),
            _: 2
          }, 1040)
        ]),
        _: 1
      }, 8, ["modelValue", "items", "rules"]),
      _createVNode(_component_v_tooltip, {
        location: "right",
        "max-width": "250"
      }, {
        activator: _withCtx(({ props }) => [
          _createVNode(_component_v_icon, _mergeProps(props, {
            color: "grey-darken-1",
            class: "ml-2 mb-3 align-center",
            style: {"font-size":"18px"}
          }), {
            default: _withCtx(() => _cache[14] || (_cache[14] = [
              _createTextVNode("mdi-information")
            ])),
            _: 2
          }, 1040)
        ]),
        default: _withCtx(() => [
          _cache[15] || (_cache[15] = _createElementVNode("span", null, "If not specified, it will match any partner.", -1))
        ]),
        _: 1
      })
    ]),
    _createElementVNode("div", _hoisted_7, [
      _createVNode(_component_v_combobox, {
        class: "flex-grow-1",
        "model-value": formModel.value.endUsers,
        "onUpdate:modelValue": onEndUsersUpdate,
        items: formModel.value.endUsers,
        label: "End Users",
        multiple: "",
        chips: "",
        "closable-chips": "",
        "hide-no-data": "",
        "menu-props": { maxWidth: 0 }
      }, {
        item: _withCtx(({ item, props }) => [
          _createVNode(_component_v_list_item, _normalizeProps(_guardReactiveProps(props)), {
            prepend: _withCtx(({ isSelected }) => [
              _createVNode(_component_v_checkbox_btn, { "model-value": isSelected }, null, 8, ["model-value"])
            ]),
            title: _withCtx(() => [
              _createVNode(_component_v_tooltip, {
                location: "right",
                text: item.raw,
                "open-delay": 1000
              }, {
                activator: _withCtx(({ props: tooltipProps }) => [
                  _createElementVNode("div", _normalizeProps(_guardReactiveProps(tooltipProps)), _toDisplayString(item.raw), 17)
                ]),
                _: 2
              }, 1032, ["text"])
            ]),
            _: 2
          }, 1040)
        ]),
        _: 1
      }, 8, ["model-value", "items"]),
      _createVNode(_component_v_tooltip, {
        location: "right",
        "max-width": "250"
      }, {
        activator: _withCtx(({ props }) => [
          _createVNode(_component_v_icon, _mergeProps(props, {
            color: "grey-darken-1",
            class: "ml-2 mb-3 align-center",
            style: {"font-size":"18px"}
          }), {
            default: _withCtx(() => _cache[16] || (_cache[16] = [
              _createTextVNode("mdi-information")
            ])),
            _: 2
          }, 1040)
        ]),
        default: _withCtx(() => [
          _cache[17] || (_cache[17] = _createElementVNode("span", null, "If not specified, it will match any end user.", -1))
        ]),
        _: 1
      })
    ]),
    _createElementVNode("div", _hoisted_8, [
      _createVNode(_component_v_checkbox, {
        modelValue: formModel.value.endUserCaseSensitive,
        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((formModel.value.endUserCaseSensitive) = $event)),
        label: "Case-sensitive match",
        density: "compact",
        "hide-details": "",
        disabled: !formModel.value.endUsers.length
      }, null, 8, ["modelValue", "disabled"]),
      _createVNode(_component_v_tooltip, {
        location: "right",
        "max-width": "300"
      }, {
        activator: _withCtx(({ props }) => [
          _createVNode(_component_v_icon, _mergeProps(props, {
            color: "grey-darken-1",
            class: "ml-2 align-center",
            style: {"font-size":"18px"}
          }), {
            default: _withCtx(() => _cache[18] || (_cache[18] = [
              _createTextVNode("mdi-information")
            ])),
            _: 2
          }, 1040)
        ]),
        default: _withCtx(() => [
          _cache[19] || (_cache[19] = _createElementVNode("span", null, "When enabled, capitalization must match. Example: an end user \"Acme\" will not match \"acme\".", -1))
        ]),
        _: 1
      })
    ]),
    _createElementVNode("div", _hoisted_9, [
      _createVNode(_component_v_checkbox, {
        modelValue: formModel.value.endUserExactMatch,
        "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((formModel.value.endUserExactMatch) = $event)),
        label: "Exact match",
        density: "compact",
        "hide-details": "",
        disabled: !formModel.value.endUsers.length
      }, null, 8, ["modelValue", "disabled"]),
      _createVNode(_component_v_tooltip, {
        location: "right",
        "max-width": "300"
      }, {
        activator: _withCtx(({ props }) => [
          _createVNode(_component_v_icon, _mergeProps(props, {
            color: "grey-darken-1",
            class: "ml-2 align-center",
            style: {"font-size":"18px"}
          }), {
            default: _withCtx(() => _cache[20] || (_cache[20] = [
              _createTextVNode("mdi-information")
            ])),
            _: 2
          }, 1040)
        ]),
        default: _withCtx(() => [
          _cache[21] || (_cache[21] = _createElementVNode("span", null, "When enabled, the whole value must match. Example: an end user \"Acme\" matches only \"Acme\", not \"Acme Corp\". When disabled, partial matches count.", -1))
        ]),
        _: 1
      })
    ]),
    _createElementVNode("div", _hoisted_10, [
      _createVNode(_unref(VDateInput), {
        modelValue: startDate.value,
        "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((startDate).value = $event)),
        label: "Start Date",
        "prepend-icon": "",
        "prepend-inner-icon": "mdi-calendar",
        min: yesterdayAuroraTime(),
        rules: [formatDates, () => !!formModel.value.startDate || 'Start date is required', validateDateRange]
      }, null, 8, ["modelValue", "min", "rules"]),
      _createVNode(_component_v_tooltip, {
        location: "right",
        class: "text-pre-wrap pt-4",
        text: `Start date is fully included in the price list, considering Chicago timezone \n(Current Chicago time: ${currentAuroraTimeString()})`
      }, {
        activator: _withCtx(({ props }) => [
          _createVNode(_component_v_icon, _mergeProps(props, {
            color: "grey-darken-1",
            class: "ml-2 mb-3 align-center",
            style: {"font-size":"18px"}
          }), {
            default: _withCtx(() => _cache[22] || (_cache[22] = [
              _createTextVNode("mdi-information")
            ])),
            _: 2
          }, 1040)
        ]),
        _: 1
      }, 8, ["text"])
    ]),
    _createElementVNode("div", _hoisted_11, [
      _createVNode(_unref(VDateInput), {
        modelValue: endDate.value,
        "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((endDate).value = $event)),
        label: "End Date",
        "prepend-icon": "",
        "prepend-inner-icon": "mdi-calendar",
        min: yesterdayAuroraTime(),
        rules: [formatDates, () => !!formModel.value.endDate || 'End date is required', validateDateRange]
      }, null, 8, ["modelValue", "min", "rules"]),
      _createVNode(_component_v_tooltip, {
        location: "right",
        class: "text-pre-wrap pt-4",
        text: `End date is fully included in the price list, considering Chicago timezone \n(Current Chicago time: ${currentAuroraTimeString()})`
      }, {
        activator: _withCtx(({ props }) => [
          _createVNode(_component_v_icon, _mergeProps(props, {
            color: "grey-darken-1",
            class: "ml-2 mb-3 align-center",
            style: {"font-size":"18px"}
          }), {
            default: _withCtx(() => _cache[23] || (_cache[23] = [
              _createTextVNode("mdi-information")
            ])),
            _: 2
          }, 1040)
        ]),
        _: 1
      }, 8, ["text"])
    ]),
    (!__props.isLimitedVersion)
      ? (_openBlock(), _createBlock(_component_v_file_input, {
          key: 0,
          accept: ".xls,.xlsx",
          modelValue: formModel.value.file,
          "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((formModel.value.file) = $event)),
          "prepend-icon": "",
          "prepend-inner-icon": "mdi-file-excel",
          label: "Import Price List File",
          placeholder: "Select a file or drag and drop here",
          "show-size": "",
          "onClick:clear": handleFileClear
        }, null, 8, ["modelValue"]))
      : _createCommentVNode("", true),
    (formModel.value.file && !__props.isEmptyPricelist)
      ? (_openBlock(), _createBlock(_component_v_radio_group, {
          key: 1,
          modelValue: formModel.value.appendMode,
          "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((formModel.value.appendMode) = $event)),
          inline: "",
          label: "Import mode"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_v_radio, { value: true }, {
              label: _withCtx(() => [
                _createElementVNode("div", _hoisted_12, [
                  _cache[26] || (_cache[26] = _createTextVNode(" Update existing price list ")),
                  _createVNode(_component_v_tooltip, {
                    location: "right",
                    "max-width": "250"
                  }, {
                    activator: _withCtx(({ props }) => [
                      _createVNode(_component_v_icon, _mergeProps({
                        size: "x-small",
                        color: "grey-darken-1",
                        class: "ml-1"
                      }, props, { style: {"font-size":"12px"} }), {
                        default: _withCtx(() => _cache[24] || (_cache[24] = [
                          _createTextVNode("mdi-information")
                        ])),
                        _: 2
                      }, 1040)
                    ]),
                    default: _withCtx(() => [
                      _cache[25] || (_cache[25] = _createElementVNode("span", null, "New part numbers will be added, existing part numbers will be updated and part numbers missing in the file will remain unchanged.", -1))
                    ]),
                    _: 1
                  })
                ])
              ]),
              _: 1
            }),
            _createVNode(_component_v_radio, { value: false }, {
              label: _withCtx(() => [
                _createElementVNode("div", _hoisted_13, [
                  _cache[29] || (_cache[29] = _createTextVNode(" Replace existing price list ")),
                  _createVNode(_component_v_tooltip, {
                    location: "right",
                    "max-width": "250"
                  }, {
                    activator: _withCtx(({ props }) => [
                      _createVNode(_component_v_icon, _mergeProps({
                        size: "x-small",
                        color: "grey-darken-1",
                        class: "ml-1"
                      }, props, { style: {"font-size":"12px"} }), {
                        default: _withCtx(() => _cache[27] || (_cache[27] = [
                          _createTextVNode("mdi-information")
                        ])),
                        _: 2
                      }, 1040)
                    ]),
                    default: _withCtx(() => [
                      _cache[28] || (_cache[28] = _createElementVNode("span", null, "All part numbers in this price list will be removed and replaced with the part numbers found in the file.", -1))
                    ]),
                    _: 1
                  })
                ])
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["modelValue"]))
      : _createCommentVNode("", true),
    _createVNode(_component_v_text_field, {
      style: {"text-align":"right"},
      modelValue: formModel.value.copperPrice,
      "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((formModel.value.copperPrice) = $event)),
      label: "Ref. Copper Price (Optional)",
      disabled: "",
      rules: [v => !v || (!isNaN(v) && v >= 0 && v.toString().trim() !== '') || 'Copper Price must be a positive number'],
      suffix: "$/Lb",
      "suffix-class": 'text-grey-darken-1'
    }, null, 8, ["modelValue", "rules"]),
    _createVNode(_component_v_textarea, {
      modelValue: formModel.value.notes,
      "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((formModel.value.notes) = $event)),
      label: "Notes",
      rows: "3",
      "auto-grow": "",
      "hide-details": "",
      class: "mb-4",
      readonly: !__props.isLimitedVersion
    }, null, 8, ["modelValue", "readonly"]),
    _createElementVNode("div", _hoisted_14, [
      _createVNode(_component_v_btn, {
        color: "secondary",
        variant: "outlined",
        onClick: handleCancel
      }, {
        default: _withCtx(() => _cache[30] || (_cache[30] = [
          _createTextVNode("Cancel")
        ])),
        _: 1
      }),
      _createVNode(_component_v_btn, {
        class: "ml-2",
        color: "primary",
        onClick: handleSaveOrPreview,
        disabled: __props.isSaveDisabled && !formModel.value.file
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(formModel.value.file ?
          'Preview' : 'Save'), 1)
        ]),
        _: 1
      }, 8, ["disabled"])
    ])
  ]))
}
}

})